import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Button, Card, Image } from "react-bootstrap";
import { getAll } from "../../api/API";
import RichTextRenderer from "../Generic/RichtextRenderer";
import LoadingSpinner from "../Generic/LoadingSpinner";
import ShareComponent from "../Generic/Reactshare";

const BlogDetails = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const processContent = (content) => {
    if (!content?.root?.children) return content;
    const newContent = { ...content, root: { ...content.root, children: [] } };

    newContent.root.children = content.root.children.map((node) => {
      if (!node.children) return node;
      const newChildren = node.children.map((child) => {
        if (child.type === "autolink" || child.type === "link") {
          const url = child.fields?.url || child.href || child.text;
          if (url && url.includes("youtube.com")) {
            return {
              type: "youtubeEmbed",
              fields: { url: getEmbedUrl(url) },
              children: [],
            };
          }
        }
        return child;
      });
      return { ...node, children: newChildren.flat() };
    });

    return newContent;
  };

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const query = { where: { slug: { equals: slug } }, depth: 2 };
        const res = await getAll("blog-posts", query);
        const blogData = res.docs?.[0] || null;
        if (blogData) blogData.content = processContent(blogData.content);
        setBlog(blogData);
      } catch (err) {
        console.error("Failed to fetch blog:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <LoadingSpinner size="4rem" text="Loading Blog..." />
      </div>
    );
  }

  if (!blog) {
    return (
      <Container className="py-5 text-center">
        <h3>Blog not found</h3>
        <Link to="/blogs">
          <Button variant="secondary" className="mt-3 rounded-pill px-4">
            Back to Blogs
          </Button>
        </Link>
      </Container>
    );
  }

  const {
    title,
    author,
    publishedDate,
    video,
    featuredImage,
    videoThumbnail,
    content,
    description,
  } = blog;

  const videoURL = video?.url;
  const mediaURL = videoURL || featuredImage?.url || videoThumbnail?.url;
  const authorName = `${author?.firstName || ""} ${
    author?.lastName || ""
  }`.trim();
  const authorImage = author?.avatar?.url;

  const shareUrl = window.location.href;
  const shareText = `Check out this article: ${title}`;

  return (
    <Container
      fluid="md"
      className="py-5 px-3 px-md-5"
      style={{ maxWidth: "1000px" }}
    >
      {/* Title, Author, Share*/}
      <Row className="align-items-center mb-4 text-center text-md-start">
        <Col xs={12} md={8}>
          <h1 className="fw-bold mb-3" style={{ fontSize: "1.9rem" }}>
            {title}
          </h1>

          <div className="d-flex flex-wrap justify-content-center justify-content-md-start align-items-center text-muted small gap-2">
            <span>By</span>
            {authorImage && (
              <Image
                src={authorImage}
                roundedCircle
                alt={authorName}
                width={30}
                height={30}
                className="mx-2"
                style={{ objectFit: "cover" }}
              />
            )}
            <strong className="text-dark">
              {authorName || "Unknown Author"}
            </strong>
            <span className="ms-2">
              {publishedDate
                ? new Date(publishedDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : ""}
            </span>
          </div>
        </Col>

        {/* SHARE SECTION */}
        <Col
          xs={12}
          md={4}
          className="d-flex justify-content-center justify-content-md-end mt-3 mt-md-0"
        >
          <ShareComponent url={shareUrl} text={shareText} title={title} />
        </Col>
      </Row>

      {/*MEDIA SECTION */}
      {mediaURL && (
        <Row className="justify-content-center mb-4">
          <Col xs={12}>
            {videoURL ? (
              <div className="ratio ratio-16x9 rounded overflow-hidden shadow-sm">
                <video
                  controls
                  poster={videoThumbnail?.url}
                  className="w-100 rounded"
                >
                  <source src={videoURL} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Img
                  src={mediaURL}
                  alt={featuredImage?.alt || title}
                  className="w-100"
                  style={{
                    objectFit: "cover",
                    height: "auto",
                    maxHeight: "450px",
                  }}
                />
              </Card>
            )}
          </Col>
        </Row>
      )}

      {/* BLOG CONTENT*/}
      <Row className="justify-content-center">
        <Col xs={12}>
          {description && (
            <p
              className="lead text-muted mb-4 text-center text-md-start"
              style={{ fontSize: "1rem" }}
            >
              {description}
            </p>
          )}

          {Array.isArray(content?.root?.children) &&
          content.root.children.length > 0 ? (
            <div className="text-wrap">
              <RichTextRenderer content={content.root.children} />
            </div>
          ) : (
            <p className="text-muted text-center">No content available</p>
          )}

          {/* BACK BUTTON */}
          <div className="text-center mt-5">
            <Link to="/blogs">
              <Button variant="secondary" className="rounded-pill px-4 py-2">
                Back to Blogs
              </Button>
            </Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default BlogDetails;
